/**
 * PlanetServicesMenu — mechanical planet. "Talk to the planet" services:
 * upgrade the mothership, repair the hull, upgrade max shield, or craft.
 */
import type { HudSnapshot } from "@/engine/GameState";
import { SHIP_DEFENSE } from "@/config/balance";
import { t, useLang } from "@/i18n";

interface Props {
  snap: HudSnapshot;
  onOpenUpgrades: () => void;
  onRepair: () => void;
  onOpenShield: () => void;
  onOpenCrafting: () => void;
  onOpenArsenal: () => void;
  onOpenRefit: () => void;
  onClose: () => void;
}

export default function PlanetServicesMenu({
  snap,
  onOpenUpgrades,
  onRepair,
  onOpenShield,
  onOpenCrafting,
  onOpenArsenal,
  onOpenRefit,
  onClose,
}: Props) {
  useLang();
  const repairCost = Math.ceil(
    (snap.maxHp - snap.hp) * SHIP_DEFENSE.repairCostPerHp,
  );
  const canRepair = repairCost > 0 && snap.minerals >= repairCost;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="stats-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Mechanical yard"
      >
        <div className="upg-head">
          <div className="upg-head-title">🔧 {t("Mechanical Yard")}</div>
          <button className="upg-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="stats-body">
          <div className="stat-row">
            <span className="k">{t("Hull")}</span>
            <span className="v">
              {Math.ceil(snap.hp)}/{snap.maxHp}
            </span>
          </div>
          <div className="stat-row">
            <span className="k">{t("Shield")}</span>
            <span className="v hi">
              {Math.ceil(snap.shield)}/{snap.maxShield}
            </span>
          </div>
          <div className="svc-actions col">
            <button className="hud-btn accent" onClick={onOpenUpgrades}>
              🔧 {t("Upgrade Mothership")}
            </button>
            <button className="hud-btn accent" onClick={onOpenRefit}>
              🚀 {t("Hull Refit")}
              {(snap.refit.nextId || snap.refit.branchIds) && snap.refit.reqsMet
                ? ` · ${t("ready")}`
                : ""}
            </button>
            <button
              className="hud-btn"
              disabled={!canRepair}
              onClick={onRepair}
            >
              ❤ {t("Repair Hull")}
              {repairCost > 0 ? ` · ${fmt(repairCost)}` : ` (${t("full")})`}
            </button>
            <button className="hud-btn" onClick={onOpenShield}>
              🛡 {t("Manage Shield Generator")}
            </button>
            <button className="hud-btn ghost" onClick={onOpenCrafting}>
              ⚒ {t("Crafting")}
            </button>
            <button
              className="hud-btn ghost"
              onClick={onOpenArsenal}
              disabled={snap.arsenal.hullTier < 1}
              title={
                snap.arsenal.hullTier < 1 ? t("Requires the Mk II hull") : undefined
              }
            >
              🔫 {t("Arsenal")}{snap.arsenal.hullTier < 1 ? " 🔒" : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function fmt(n: number): string {
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "k";
  return Math.floor(n).toString();
}
